import type { AssetKind, BrandKit } from './types';

const ASSET_KINDS = ['logo', 'frame', 'outro'] as const satisfies readonly AssetKind[];
const ASSET_LABELS: Record<AssetKind, string> = {
  logo: 'Логотип',
  frame: 'Рамка',
  outro: 'Заглушка',
};

const disabledByKit = new Map<string, Set<AssetKind>>();
const availableByKit = new Map<string, Set<AssetKind>>();

export function isBrandAssetEnabled(kitId: string, kind: AssetKind): boolean {
  return !disabledByKit.get(kitId)?.has(kind);
}

export function setBrandAssetEnabled(kitId: string, kind: AssetKind, enabled: boolean): void {
  const disabled = disabledByKit.get(kitId) ?? new Set<AssetKind>();
  if (enabled) {
    disabled.delete(kind);
  } else {
    disabled.add(kind);
  }
  if (disabled.size) {
    disabledByKit.set(kitId, disabled);
  } else {
    disabledByKit.delete(kitId);
  }
}

function filteredAssets(kit: BrandKit): BrandKit['assets'] {
  const target = kit.assets;
  return new Proxy(target, {
    ownKeys(source) {
      return Reflect.ownKeys(source).filter((key) => {
        if (typeof key !== 'string' || !ASSET_KINDS.includes(key as AssetKind)) {
          return true;
        }
        return isBrandAssetEnabled(kit.id, key as AssetKind);
      });
    },
    getOwnPropertyDescriptor(source, property) {
      if (
        typeof property === 'string' &&
        ASSET_KINDS.includes(property as AssetKind) &&
        !isBrandAssetEnabled(kit.id, property as AssetKind)
      ) {
        return undefined;
      }
      return Reflect.getOwnPropertyDescriptor(source, property);
    },
  });
}

export function wrapBrandKitsWithAssetSelection(kits: BrandKit[]): BrandKit[] {
  availableByKit.clear();
  return kits.map((kit) => {
    const available = new Set<AssetKind>();
    for (const kind of ASSET_KINDS) {
      if (kit.assets[kind]) {
        available.add(kind);
      }
    }
    availableByKit.set(kit.id, available);
    return { ...kit, assets: filteredAssets(kit) };
  });
}

function installStyles(): void {
  if (document.getElementById('assetSelectionStyles')) {
    return;
  }
  const style = document.createElement('style');
  style.id = 'assetSelectionStyles';
  style.textContent = `
    .asset-choice-box { margin-top: -2px; }
    .asset-choice-title { display:block; margin-bottom:7px; color:var(--muted); font-size:12px; }
    .asset-choice-list { display:flex; flex-wrap:wrap; gap:7px; }
    .asset-choice { display:flex; align-items:center; gap:7px; min-height:36px; padding:7px 10px; border:1px solid #ffffff18; border-radius:11px; background:#ffffff08; font-size:13px; font-weight:750; cursor:pointer; user-select:none; }
    .asset-choice.is-active { border-color:#ffffff38; background:#ffffff13; }
    .asset-choice.is-unavailable { opacity:.38; cursor:default; }
    .asset-choice input { width:17px; height:17px; margin:0; accent-color:var(--accent); }
  `;
  document.head.append(style);
}

function installAssetSelector(): void {
  const kitSelect = document.getElementById('kitSelect');
  if (!(kitSelect instanceof HTMLSelectElement) || document.getElementById('assetChoiceBox')) {
    return;
  }

  installStyles();
  const box = document.createElement('div');
  box.id = 'assetChoiceBox';
  box.className = 'asset-choice-box';
  box.hidden = true;

  const title = document.createElement('small');
  title.className = 'asset-choice-title';
  title.textContent = 'Что добавить в кружок';
  const list = document.createElement('div');
  list.className = 'asset-choice-list';

  const controls = new Map<AssetKind, { label: HTMLLabelElement; input: HTMLInputElement }>();
  for (const kind of ASSET_KINDS) {
    const label = document.createElement('label');
    label.className = 'asset-choice';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.dataset.assetChoice = kind;
    const text = document.createElement('span');
    text.textContent = ASSET_LABELS[kind];
    label.append(input, text);
    list.append(label);
    controls.set(kind, { label, input });

    input.addEventListener('change', () => {
      const kitId = kitSelect.value;
      if (!kitId || input.disabled) {
        return;
      }
      setBrandAssetEnabled(kitId, kind, input.checked);
      kitSelect.dispatchEvent(new Event('change', { bubbles: true }));
      queueMicrotask(sync);
    });
  }
  box.append(title, list);

  const field = kitSelect.closest('.field');
  field?.insertAdjacentElement('afterend', box);

  const sync = (): void => {
    const kitId = kitSelect.value;
    const available = availableByKit.get(kitId);
    box.hidden = !kitId || !available?.size;
    for (const kind of ASSET_KINDS) {
      const control = controls.get(kind);
      if (!control) continue;
      const exists = Boolean(available?.has(kind));
      control.input.disabled = !exists;
      control.input.checked = exists && isBrandAssetEnabled(kitId, kind);
      control.label.classList.toggle('is-active', control.input.checked);
      control.label.classList.toggle('is-unavailable', !exists);
    }
    const logoControls = document.getElementById('logoControls');
    if (logoControls instanceof HTMLElement) {
      logoControls.hidden = !kitId || !available?.has('logo') || !isBrandAssetEnabled(kitId, 'logo');
    }
  };

  kitSelect.addEventListener('change', () => queueMicrotask(sync));
  new MutationObserver(() => queueMicrotask(sync)).observe(kitSelect, { childList: true });
  window.addEventListener('krug:billing-updated', () => queueMicrotask(sync));
  sync();
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installAssetSelector, { once: true });
  } else {
    installAssetSelector();
  }
}
